import {
  Banknote,
  CheckCircle2,
  Code2,
  Copy,
  ExternalLink,
  Eye,
  File,
  RefreshCw,
  RotateCcw,
  SkipForward,
  XCircle,
  Zap,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import type { Installment, InstallmentStatus } from "@/types/installment";
import type { Mandate } from "@/types/mandate";
import { formatDateTime, formatDueDate, formatINR } from "@/pages/retailer/installments/data";
import { retailerById } from "../data";
import InstallmentStatusBadge from "@/pages/retailer/installments/components/InstallmentStatusBadge";
import { rocketpayService, installmentService } from "@/services/apiServices";
import { useToast } from "@/components/ui/Toast";

const COLLECTED: InstallmentStatus[] = ["COLLECTED", "SETTLEMENT_PENDING", "SETTLED", "SETTLEMENT_FAILED"];

interface Props {
  open: boolean;
  onClose: () => void;
  installment: Installment | null;
  mandate: Mandate | null;
  onCopyUtr?: (utr: string) => void;
  onRefresh?: (inst: Installment) => void;
  onRetry?: (inst: Installment) => void;
  onSkip?: (inst: Installment) => void;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-[#F1F5F9] py-3 last:border-0">
      <span className="text-[13px] text-ink-secondary">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{children}</span>
    </div>
  );
}

export default function AdminInstallmentDetailModal({
  open,
  onClose,
  installment,
  mandate,
  onCopyUtr,
  onRefresh,
  onRetry,
  onSkip,
}: Props) {
  const navigate = useNavigate();
  const toast = useToast();
  const [receiptPreviewOpen, setReceiptPreviewOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [rawJsonOpen, setRawJsonOpen] = useState(false);
  const [rawJsonLoading, setRawJsonLoading] = useState(false);
  const [liveRpData, setLiveRpData] = useState<any>(null);

  if (!installment) return null;

  const collected = COLLECTED.includes(installment.status);
  const failed = installment.status === "FAILED";
  const offline = installment.status === "COLLECTED_OFFLINE";
  const settlementStage =
    installment.status === "SETTLED" ||
    installment.status === "SETTLEMENT_PENDING" ||
    installment.status === "SETTLEMENT_FAILED";
  const retailer = retailerById(installment.retailerId);
  const retryable = failed || installment.status === "FAILED";
  const skippable = installment.status === "SCHEDULED";

  const effectiveRpId = installment.rocketpayInstallmentId || (installment as any).rawResponse?.id || null;

  async function handleRefreshClick() {
    setSyncing(true);
    try {
      if (onRefresh) {
        await onRefresh(installment!);
      } else {
        const res = await installmentService.refresh(installment!.id);
        toast.success(res?.message || "Installment status synced with gateway");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to refresh installment from gateway");
    } finally {
      setSyncing(false);
    }
  }

  async function handleInspectLiveRocketPay() {
    setRawJsonOpen(true);
    setRawJsonLoading(true);
    try {
      const rpId = effectiveRpId || installment!.id;
      const res = await rocketpayService.getInstallmentByIdV4(rpId);
      const data = res?.data ?? res;
      setLiveRpData(data);
    } catch (err: any) {
      setLiveRpData(installment!.rawResponse || { error: err?.message || "Failed to fetch live gateway installment" });
    } finally {
      setRawJsonLoading(false);
    }
  }

  function copyText(text: string, label = "Copied") {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} copied to clipboard`))
      .catch(() => toast.error("Could not copy"));
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Installment details"
        size="md"
        footer={
          <div className="flex flex-wrap items-center justify-between gap-2.5 border-t border-line px-5 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                loading={syncing}
                leftIcon={<RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />}
                onClick={handleRefreshClick}
                title="Synchronize latest live status with Gateway"
              >
                Sync Gateway
              </Button>
              {retryable && onRetry && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<RotateCcw className="h-4 w-4 text-warning" />}
                  onClick={() => onRetry(installment)}
                >
                  Retry Collection
                </Button>
              )}
              {skippable && onSkip && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<SkipForward className="h-4 w-4 text-ink-muted" />}
                  onClick={() => onSkip(installment)}
                >
                  Skip
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
              <Button
                onClick={() => {
                  onClose();
                  navigate(`/admin/mandates/${installment.refNo}`);
                }}
              >
                View Mandate
              </Button>
            </div>
          </div>
        }
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[13px] text-ink-secondary">{installment.installmentId}</span>
            <button
              type="button"
              onClick={() => copyText(installment.installmentId, "Installment ID")}
              className="p-1 hover:bg-slate-100 rounded text-ink-muted hover:text-ink cursor-pointer"
              title="Copy ID"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
          <InstallmentStatusBadge status={installment.status} />
        </div>

        {/* Gateway Integration Card */}
        {/* <div className="mt-3 rounded-lg border border-primary-200 bg-gradient-to-br from-primary-50/50 to-sky-50/30 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary-600" />
              <p className="text-xs font-bold uppercase tracking-wider text-primary-700">Gateway Integration</p>
            </div>
            <button
              type="button"
              onClick={handleInspectLiveRocketPay}
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-800 hover:underline cursor-pointer"
            >
              <Code2 className="h-3.5 w-3.5" />
              Inspect Live JSON
            </button>
          </div>

          <div className="mt-3 space-y-2 text-xs">
            <div className="flex items-center justify-between border-b border-primary-100 pb-1.5">
              <span className="text-ink-secondary">Gateway Installment ID</span>
              <div className="flex items-center gap-1.5 font-mono font-medium text-ink">
                <span>{effectiveRpId || "Not assigned yet"}</span>
                {effectiveRpId && (
                  <button
                    type="button"
                    onClick={() => copyText(effectiveRpId, "Gateway ID")}
                    className="p-1 hover:bg-primary-100 rounded text-primary-700 cursor-pointer"
                    title="Copy Gateway ID"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between border-b border-primary-100 pb-1.5">
              <span className="text-ink-secondary">Collection Channel</span>
              <span className="font-semibold text-primary-800">
                {installment.collectionMode === "OFFLINE" ? "OFFLINE (Cash/Store)" : "UPI AutoPay / Bank NACH"}
              </span>
            </div>
            <div className="flex items-center justify-between pt-0.5">
              <span className="text-ink-secondary">Live Sync Action</span>
              <button
                type="button"
                onClick={handleRefreshClick}
                disabled={syncing}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Refreshing..." : "Refresh Status"}
              </button>
            </div>
          </div>
        </div> */}

        <div className="mt-3 rounded-lg border border-line bg-page p-4">
          <p className="text-sm font-semibold text-ink">Installment Details</p>
          <Row label="Installment Number">
            {installment.installmentNumber} of {installment.totalInstallments}
          </Row>
          <Row label="Amount">{formatINR(installment.amount)}</Row>
          <Row label="Due Date">{formatDueDate(installment.dueDate)}</Row>
          <Row label="Retry Count">{installment.retryCount}</Row>
        </div>

        {offline ? (
          <>
            <div className="mt-4 rounded-lg border border-line bg-page p-4">
              <p className="text-sm font-semibold text-ink">Offline Collection</p>
              <Row label="Amount Received">
                {installment.offlineAmountReceived != null ? formatINR(installment.offlineAmountReceived) : formatINR(installment.amount)}
              </Row>
              <Row label="Collection Mode">{installment.offlineMode ?? "—"}</Row>
              <Row label="Reference Number">{installment.offlineReference ?? "—"}</Row>
              <Row label="Collection Date">
                {installment.offlineCollectedOn ? formatDateTime(installment.offlineCollectedOn) : "—"}
              </Row>
              <Row label="Recorded By">{installment.offlineRecordedBy ?? "—"}</Row>
              <Row label="Remarks">{installment.offlineRemark || "—"}</Row>
              {installment.offlineReceipt && (
                <Row label="Receipt">
                  <span className="inline-flex items-center justify-end gap-2">
                    <span className="flex items-center gap-1.5 rounded-md border border-line bg-white px-2 py-1">
                      <File className="h-3.5 w-3.5 text-ink-muted" />
                      <span className="max-w-[150px] truncate text-xs text-ink-secondary">{installment.offlineReceipt}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setReceiptPreviewOpen(true)}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 cursor-pointer"
                      aria-label="Preview receipt"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </Row>
              )}
            </div>
            <div className="mt-4 flex items-start gap-3 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] p-4">
              <Banknote className="mt-0.5 h-5 w-5 shrink-0 text-[#1E40AF]" />
              <p className="text-[13px] text-ink">
                This installment was collected directly by the retailer. No platform or provider charge was applied.
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="mt-4 rounded-lg border border-line bg-page p-4">
              <p className="text-sm font-semibold text-ink">Collection</p>
              <Row label="Presented On">{installment.presentedAt ? formatDateTime(installment.presentedAt) : "—"}</Row>
              {collected ? (
                <>
                  <Row label="Collected On">
                    {installment.settledAt ? formatDateTime(installment.settledAt) : installment.presentedAt ? formatDateTime(installment.presentedAt) : "—"}
                  </Row>
                  <Row label="UTR Number">
                    <span className="inline-flex items-center justify-end gap-2">
                      <span className="font-mono text-[13px]">{installment.utrNumber}</span>
                      {onCopyUtr && installment.utrNumber && (
                        <button
                          type="button"
                          onClick={() => onCopyUtr(installment.utrNumber!)}
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 cursor-pointer"
                          aria-label="Copy UTR"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  </Row>
                </>
              ) : failed ? (
                <>
                  <Row label="Failure Reason">{installment.failureReason ?? "—"}</Row>
                  <Row label="Failure Code">{installment.failureCode ?? "—"}</Row>
                </>
              ) : (
                <Row label="Collected On">—</Row>
              )}
            </div>

            {failed ? (
              <div className="mt-4 flex items-start gap-3 rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-4">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
                <p className="text-[13px] text-ink">
                  Collection failed, so no settlement was attempted for this installment. Use the Retry button to schedule a retry.
                </p>
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-line bg-page p-4">
                <p className="text-sm font-semibold text-ink">Settlement</p>
                <Row label="Settlement Status">
                  {settlementStage ? (
                    <InstallmentStatusBadge status={installment.status} size="sm" />
                  ) : (
                    <span className="text-[#94A3B8]">—</span>
                  )}
                </Row>
                <Row label="Settled On">{installment.settledAt ? formatDateTime(installment.settledAt) : "—"}</Row>
                <Row label="Settlement Amount">{collected ? formatINR(installment.settlementAmount) : "—"}</Row>
                <Row label="Provider Charge">{collected ? formatINR(installment.providerCharge) : "—"}</Row>
                <Row label="Platform Charge">{collected ? formatINR(installment.platformCharge) : "—"}</Row>
                <p className="pt-2 text-xs text-ink-secondary">
                  Settlement amount is credited to the retailer's registered bank account.
                </p>
              </div>
            )}
          </>
        )}

        <div className="mt-4 rounded-lg border border-line bg-page p-4">
          <p className="text-sm font-semibold text-ink">Mandate</p>
          <Row label="Reference Number">
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(`/admin/mandates/${installment.refNo}`);
              }}
              className="font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
            >
              {installment.refNo}
            </button>
          </Row>
          <Row label="Retailer">
            <button
              type="button"
              onClick={() => navigate(`/admin/retailers/${installment.retailerId}`)}
              className="font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
            >
              {installment.shopName || installment.retailerName || retailer?.shopName || installment.retailerId}
            </button>
          </Row>
          <Row label="Customer Name">{installment.customerName}</Row>
          <Row label="Customer Mobile">{installment.customerMobile}</Row>
          <Row label="Bank Name">{installment.bankName}</Row>
          <Row label="UMRN">
            <span className="font-mono text-[13px]">{mandate?.umrn ?? "—"}</span>
          </Row>
        </div>

        <Modal
          open={receiptPreviewOpen}
          onClose={() => setReceiptPreviewOpen(false)}
          title="Receipt"
          size="sm"
          footer={
            <div className="flex items-center justify-end border-t border-line px-5 py-4">
              <Button variant="secondary" onClick={() => setReceiptPreviewOpen(false)}>
                Close
              </Button>
            </div>
          }
        >
          <div className="flex flex-col items-center justify-center gap-3 py-10">
            <File className="h-10 w-10 text-ink-muted" />
            <p className="text-sm text-ink">{installment.offlineReceipt}</p>
            <p className="text-xs text-ink-secondary">Receipt images cannot be previewed in this demo.</p>
          </div>
        </Modal>
      </Modal>

      {/* Live Gateway Installment JSON Inspector Modal */}
      <Modal
        open={rawJsonOpen}
        onClose={() => setRawJsonOpen(false)}
        title="Live Gateway Installment Object"
        size="lg"
        footer={
          <div className="flex items-center justify-between border-t border-line px-5 py-4">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Copy className="h-4 w-4" />}
              onClick={() => copyText(JSON.stringify(liveRpData ?? installment.rawResponse, null, 2), "JSON payload")}
            >
              Copy JSON
            </Button>
            <Button variant="primary" size="sm" onClick={() => setRawJsonOpen(false)}>
              Close
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between bg-slate-900 text-slate-200 px-3 py-2 rounded-t-md text-xs font-mono">
            <span>GET /v4/installments/{effectiveRpId || installment.id}</span>
            <span className="text-emerald-400">STATUS: {installment.status}</span>
          </div>
          {rawJsonLoading ? (
            <div className="p-12 text-center text-sm text-ink-muted flex items-center justify-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" /> Fetching live response from Gateway...
            </div>
          ) : (
            <pre className="max-h-96 overflow-auto rounded-b-md bg-slate-950 p-4 font-mono text-xs text-emerald-400 border border-slate-800">
              {JSON.stringify(liveRpData || installment.rawResponse || { notice: "No raw response object recorded yet." }, null, 2)}
            </pre>
          )}
        </div>
      </Modal>
    </>
  );
}